import paper from "paper";
import { ToolBase } from "./ToolBase";
import type { PointerSample } from "../canvas/PointerInput";
import { currentPointerKind, snapRadiusFor } from "../canvas/pointerPrecision";
import {
  projectConstructionLines,
  type ConstructionLine,
  type ConstructionOutline,
  type OutlineSegmentRef,
  type Vec2,
} from "../model/Project";
import { buildConstructionOutlinePath } from "../model/constructionOutline";
import { snapTrace, legArcParams, type TraceSnap } from "./traceSnap";
import {
  commitConstructionShape,
  isPointOnLeather,
  type ShapeMode,
} from "./constructionShapeMode";
import { cssVar } from "../colors";

/** Snap / close radius in screen px (converted to project units via zoom). */
const SNAP_PX = 12;

/** Minimum enclosed area (mm²) for a traced outline to count as a real piece —
 *  rejects degenerate / non-closed slivers (e.g. two legs retracing one line),
 *  so an incomplete shape is never committed. */
const MIN_PIECE_AREA = 1;

/**
 * Magnetic trace tool (#650): click point-to-point along construction geometry —
 * the cursor snaps to anchors, intersections, and along-line points — and close
 * the loop to create a leather piece whose outline is a `constructionOutline`.
 * Editing the source lines afterward re-derives the piece (see
 * `deriveConstructionOutlines`). Each leg follows a construction line shared by
 * its two endpoints, so a segment shared between parts links to both.
 */
export class TraceTool extends ToolBase {
  id = "trace";
  label = "Trace";
  shortcut = "t";
  cursor = "crosshair";

  /** What a closed trace becomes — leather piece / cutout / stitch line. */
  private mode: ShapeMode = "leather";
  private vertices: TraceSnap[] = [];
  private legs: OutlineSegmentRef[] = [];
  private hoverSnap: TraceSnap | null = null;

  setMode(mode: ShapeMode) {
    this.mode = mode;
  }
  /** Line the cursor was last tracing along — picks which shared line a leg
   *  follows when its endpoints lie on more than one. */
  private hoverLineId: string | null = null;
  private previewItems: paper.Item[] = [];
  /** Notified when the draft's vertex count changes so the done/cancel
   *  fly-out can show / hide / re-enable (mirrors Stitch & Construction). */
  private draftChange?: () => void;

  /** Number of committed trace vertices (drives the fly-out). */
  get vertexCount(): number {
    return this.vertices.length;
  }

  setDraftChangeListener(fn: () => void) {
    this.draftChange = fn;
  }

  /** Construction-line ids the in-progress outline references so far (leg
   *  sources + intersection endpoint lines), in order — mirrors `tracedLineIds`
   *  for a committed part so the panel can list the draft's segments live. */
  draftLineIds(): string[] {
    const ids: string[] = [];
    const seen = new Set<string>();
    const add = (id: string) => {
      if (!seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    };
    for (const leg of this.legs) {
      add(leg.sourceId);
      for (const ep of [leg.start, leg.end]) {
        if (ep.kind === "intersection") {
          add(ep.aId);
          add(ep.bId);
        } else {
          add(ep.constructionId);
        }
      }
    }
    return ids;
  }

  override onActivate() {
    // Leave committed pieces fully filled (the normal / selection look) —
    // construction lines render above the leather, so they stay visible to
    // trace along even over filled parts.
    this.reset();
  }

  override onDeactivate() {
    this.reset();
  }

  override onCancel() {
    this.reset();
  }

  private reset() {
    this.vertices = [];
    this.legs = [];
    this.hoverSnap = null;
    this.clearPreview();
    this.draftChange?.();
  }

  private lines(): ConstructionLine[] {
    return projectConstructionLines(this.ctx.history.state);
  }

  /** A finger reaches `COARSE_SNAP_SCALE`× as far (#1150). */
  private threshold(): number {
    return (
      snapRadiusFor(SNAP_PX, currentPointerKind()) /
      Math.max(0.01, this.ctx.viewport.zoom)
    );
  }

  private snapAt(p: Vec2): TraceSnap | null {
    return snapTrace(
      this.lines(),
      p,
      this.threshold(),
      this.ctx.scene.getPaperScope(),
    );
  }

  /** True when `pt` sits on a visible leather piece — gates cutout / stitch
   *  vertices to the leather (#703). */
  private onLeather(pt: Vec2): boolean {
    return isPointOnLeather(
      this.ctx.history.state,
      this.ctx.scene.getPaperScope(),
      pt,
    );
  }

  override onDown(_s: PointerSample, p: { x: number; y: number }) {
    const snap = this.snapAt(p);
    if (!snap) return; // trace only along construction geometry

    // A cutout / stitch must live on a leather piece, so reject a vertex picked
    // off the leather (#703). Leather mode traces new pieces, so it's exempt.
    if (this.mode !== "leather" && !this.onLeather(snap.point)) return;

    if (this.vertices.length === 0) {
      this.vertices.push(snap);
      this.updatePreview();
      this.draftChange?.();
      return;
    }

    // Click near the first vertex closes the loop.
    const first = this.vertices[0]!;
    if (
      this.vertices.length >= 2 &&
      this.dist(snap.point, first.point) <= this.threshold()
    ) {
      this.tryClose();
      return;
    }

    const prev = this.vertices[this.vertices.length - 1]!;
    const leg = this.computeLeg(prev, snap);
    if (!leg) return; // endpoints share no construction line → not magnetic

    const lastLeg = this.legs[this.legs.length - 1];
    if (
      lastLeg &&
      lastLeg.sourceId === leg.sourceId &&
      lastLeg.pathIndex === leg.pathIndex
    ) {
      // Still tracing the SAME construction line — the intermediary click adds
      // nothing (the leg already follows the line through it), so collapse it:
      // extend the previous leg's end to the new snap instead of keeping the
      // in-between vertex. Keep the leg's original arc direction (#661): once the
      // trace has committed to a way round a closed line, later clicks along it
      // must not flip to the "shorter" arc just because the accumulated span
      // grew past the line's half-way point.
      this.legs[this.legs.length - 1] = { ...lastLeg, end: snap.ref };
      this.vertices[this.vertices.length - 1] = snap;
    } else {
      this.legs.push(leg);
      this.vertices.push(snap);
    }
    this.updatePreview();
    this.draftChange?.();
  }

  override onHover(_s: PointerSample, p: { x: number; y: number }) {
    this.updateHover(p);
  }

  override onMove(_s: PointerSample, p: { x: number; y: number }) {
    this.updateHover(p);
  }

  private updateHover(p: Vec2) {
    const snap = this.snapAt(p);
    // In cutout / stitch mode, don't show a snap dot where a click would be
    // rejected for being off the leather (#703).
    this.hoverSnap =
      snap && (this.mode === "leather" || this.onLeather(snap.point))
        ? snap
        : null;
    if (this.hoverSnap?.nearestLineId) {
      this.hoverLineId = this.hoverSnap.nearestLineId;
    }
    this.updatePreview();
    // Keep the fly-out's confirm enabled-state live as the cursor approaches a
    // valid close (the closing leg + area depend on the hover).
    if (this.vertices.length > 0) this.draftChange?.();
  }

  /** Build a leg prev→cur following a construction line shared by both. */
  private computeLeg(
    prev: TraceSnap,
    cur: TraceSnap,
  ): OutlineSegmentRef | null {
    const shared = prev.lines.filter((id) => cur.lines.includes(id));
    if (shared.length === 0) return null;
    const sourceId =
      this.hoverLineId && shared.includes(this.hoverLineId)
        ? this.hoverLineId
        : shared[0]!;
    const line = this.lines().find((l) => l.id === sourceId);
    if (!line) return null;
    const { pathIndex, reversed } = legArcParams(
      line,
      prev.point,
      cur.point,
      this.ctx.scene.getPaperScope(),
    );
    return { sourceId, pathIndex, start: prev.ref, end: cur.ref, reversed };
  }

  /** The closed outline the current draft would produce, or null if it isn't a
   *  real enclosed piece yet — needs ≥ 2 vertices and a non-degenerate area, so
   *  a zero-area sliver is never committed. The shape is auto-closed: if the
   *  last and first vertices share a construction line the closing edge follows
   *  it (magnetic) — correct when closing around a CLOSED line, whose
   *  complementary arc encloses area. But when that magnetic close just
   *  retraces the trace and collapses the area (a single OPEN polyline, whose
   *  ends share the line but only the long way back), fall back to closing
   *  straight to the start (the deriver's implicit close on `closed: true`,
   *  #756). */
  private prospectiveClosed(): {
    outline: ConstructionOutline;
    path: string;
  } | null {
    if (this.vertices.length < 2) return null;
    const last = this.vertices[this.vertices.length - 1]!;
    const first = this.vertices[0]!;
    const scope = this.ctx.scene.getPaperScope();
    const magnetic = this.computeLeg(last, first);
    // Try the magnetic closing edge first, then the straight close; take the
    // first that encloses a real area.
    for (const closing of magnetic ? [magnetic, null] : [null]) {
      const outline: ConstructionOutline = {
        segments: closing ? [...this.legs, closing] : [...this.legs],
        closed: true,
      };
      const path = buildConstructionOutlinePath(outline, this.lines(), scope);
      if (!path) continue;
      let area = 0;
      try {
        const cp = new scope.CompoundPath({ pathData: path, insert: false });
        area = Math.abs(cp.area);
        cp.remove();
      } catch {
        continue;
      }
      if (area >= MIN_PIECE_AREA) return { outline, path };
    }
    return null;
  }

  /** True when the draft currently forms a real closed piece — drives the
   *  close-on-click gate (and the confirm button in leather / cutout modes). */
  canClose(): boolean {
    return this.prospectiveClosed() != null;
  }

  /** The OPEN outline the current draft would produce — exactly the traced
   *  legs, with no auto-closing edge. Stitch lines (#675) don't need an
   *  enclosed area, so this only needs ≥ 1 leg and a buildable path. Returns
   *  null otherwise. */
  private prospectiveOpen(): {
    outline: ConstructionOutline;
    path: string;
  } | null {
    if (this.legs.length < 1) return null;
    const outline: ConstructionOutline = {
      segments: [...this.legs],
      closed: false,
    };
    const scope = this.ctx.scene.getPaperScope();
    const path = buildConstructionOutlinePath(outline, this.lines(), scope);
    if (!path) return null;
    return { outline, path };
  }

  /** True when the fly-out confirm / Enter can commit. Stitch mode commits the
   *  OPEN traced seam (no enclosed area needed); leather / cutout require a
   *  closed loop. */
  canCommit(): boolean {
    return this.mode === "stitch"
      ? this.prospectiveOpen() != null
      : this.canClose();
  }

  private tryClose(): boolean {
    const res = this.prospectiveClosed();
    if (!res) return false; // never commit an incomplete / non-closed shape
    // Create the entity for the active mode (piece / cutout / stitch). A
    // cutout / stitch with no parent piece refuses — leave the draft intact.
    if (!commitConstructionShape(this.ctx, res.outline, this.mode))
      return false;
    this.reset();
    return true;
  }

  /** Commit via the fly-out confirm / Enter. In stitch mode this commits the
   *  OPEN seam exactly as traced (#675) — closing only happens when the user
   *  explicitly clicks back on the start point; leather / cutout close the
   *  loop. */
  private tryCommit(): boolean {
    if (this.mode === "stitch") {
      const res = this.prospectiveOpen();
      if (!res) return false;
      if (!commitConstructionShape(this.ctx, res.outline, this.mode)) {
        return false;
      }
      this.reset();
      return true;
    }
    return this.tryClose();
  }

  /** Enter / fly-out confirm → finish the trace. True when it committed. */
  handleFinish(): boolean {
    if (this.vertices.length < 2) return false;
    return this.tryCommit();
  }

  /** Escape → discard the in-progress trace. True when there was one. */
  handleCancel(): boolean {
    if (this.vertices.length === 0) return false;
    this.reset();
    return true;
  }

  private dist(a: Vec2, b: Vec2): number {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  // ---------------------------------------------------------------- preview

  private clearPreview() {
    for (const it of this.previewItems) it.remove();
    this.previewItems = [];
  }

  private strokeAlong(d: string, dashed: boolean, accent: string) {
    const scope = this.ctx.scene.getPaperScope();
    const item = new scope.CompoundPath({ pathData: d });
    item.strokeColor = new scope.Color(accent);
    item.strokeWidth = 1.5;
    item.strokeScaling = false;
    if (dashed) item.dashArray = [4, 2];
    item.fillColor = null;
    item.data = { ephemeral: true };
    this.previewItems.push(item);
  }

  private updatePreview() {
    const scope = this.ctx.scene.getPaperScope();
    this.clearPreview();
    const accent = cssVar("--canvas-accent");
    // In Cutout mode the draft should read like a cutout, not a leather piece
    // (#701): the committed outline is drawn in the cut colour and the enclosed
    // fill matches the rendered cutout (translucent black + dashed cut edge).
    const cutoutMode = this.mode === "cutout";
    const outline = cutoutMode ? cssVar("--piece-cut") : accent;
    const lines = this.lines();

    // Live leg (last committed vertex → hover snap), computed once and shared
    // by the fill and the dashed stroke below.
    const prev =
      this.vertices.length > 0
        ? this.vertices[this.vertices.length - 1]!
        : null;
    const liveLeg =
      prev && this.hoverSnap ? this.computeLeg(prev, this.hoverSnap) : null;

    // Filled prospective piece — EXACTLY the shape a finish/close would commit
    // (auto-closed: a magnetic closing edge when the ends share a line, else a
    // straight edge back to the start). It appears precisely when the draft is
    // a real closed piece — which is also when Enter / the fly-out confirm will
    // finish it. The live leg below shows where the next click would extend it.
    // Stitch mode commits an OPEN seam (#675), so it shows no filled-piece
    // preview — the solid leg stroke below is exactly what will commit.
    const closed = this.mode === "stitch" ? null : this.prospectiveClosed();
    if (closed) {
      const fill = new scope.CompoundPath({ pathData: closed.path });
      if (cutoutMode) {
        // Match the committed cutout look — translucent black fill + dashed
        // cut-coloured edge — so tracing a cutout previews as one (#701).
        fill.fillColor = new scope.Color(0, 0, 0, 0.25);
        fill.strokeColor = new scope.Color(outline);
        fill.strokeWidth = 1;
        fill.strokeScaling = false;
        fill.dashArray = [3, 3];
      } else {
        const col = new scope.Color(cssVar("--leather-fill"));
        col.alpha *= 0.7; // slightly softened so it reads as in-progress
        fill.fillColor = col;
        fill.strokeColor = null;
      }
      fill.data = { ephemeral: true };
      this.previewItems.push(fill);
    }

    // Committed outline so far (solid stroke, on top of the fill).
    if (this.legs.length > 0) {
      const d = buildConstructionOutlinePath(
        { segments: this.legs, closed: false },
        lines,
        scope,
      );
      if (d) this.strokeAlong(d, false, outline);
    }

    // Live leg from the last committed vertex to the hover snap.
    if (prev && this.hoverSnap) {
      if (liveLeg) {
        const d = buildConstructionOutlinePath(
          { segments: [liveLeg], closed: false },
          lines,
          scope,
        );
        if (d) this.strokeAlong(d, true, accent);
      } else {
        // No shared line → muted straight rubber-band so the user sees the
        // click won't land as a magnetic leg.
        const rb = new scope.Path.Line(
          new scope.Point(prev.point.x, prev.point.y),
          new scope.Point(this.hoverSnap.point.x, this.hoverSnap.point.y),
        );
        rb.strokeColor = new scope.Color(accent);
        rb.strokeColor.alpha = 0.3;
        rb.strokeWidth = 1;
        rb.strokeScaling = false;
        rb.dashArray = [2, 3];
        rb.data = { ephemeral: true };
        this.previewItems.push(rb);
      }
    }

    // Snap indicator at the hover point — same size / style as the other snap
    // indicators (e.g. the construction draft snap point: radius 2.0 with a
    // constant-width stroke). Slightly more translucent for a plain along-line
    // point than for a specific anchor / intersection snap.
    if (this.hoverSnap) {
      const ring = new scope.Path.Circle(
        new scope.Point(this.hoverSnap.point.x, this.hoverSnap.point.y),
        2.0,
      );
      const fill = new scope.Color(accent);
      fill.alpha = this.hoverSnap.kind === "along" ? 0.25 : 0.45;
      ring.fillColor = fill;
      ring.strokeColor = new scope.Color(accent);
      ring.strokeWidth = 2.2;
      ring.strokeScaling = false;
      ring.data = { ephemeral: true };
      this.previewItems.push(ring);
    }

    // Close-to-start ring.
    if (
      this.vertices.length >= 2 &&
      this.hoverSnap &&
      this.dist(this.hoverSnap.point, this.vertices[0]!.point) <=
        this.threshold()
    ) {
      const first = this.vertices[0]!;
      const ring = new scope.Path.Circle(
        new scope.Point(first.point.x, first.point.y),
        this.threshold(),
      );
      ring.strokeColor = new scope.Color(accent);
      ring.strokeWidth = 1;
      ring.strokeScaling = false;
      ring.fillColor = null;
      ring.data = { ephemeral: true };
      this.previewItems.push(ring);
    }
  }
}
